import { useMemo, useState } from 'react';
import { crossedMilestones, track } from '@/lib/analytics';
import type {
  AnswerOption,
  Answers,
} from '../../domain/entities/answer.entity';
import {
  toAnswersByQuestionId,
  toSimuladoQuestions,
} from '../../domain/entities/question.entity';
import { useExam } from '../hooks/use-exam';
import { useSimuladoDraft } from '../hooks/use-simulado-draft';
import { EnsaioAnswersModal } from './EnsaioAnswersModal';
import { FinishConfirmModal } from './FinishConfirmModal';
import { EnsaioDownloadLink, useEnsaioMaterial } from './EnsaioMaterials';
import { QuestionStepper } from './QuestionStepper';

interface EnsaioRunnerProps {
  /** Respostas por `questionId`, depois da confirmação de finalizar. */
  onFinish: (answers: Record<string, number>) => void;
  isSubmitting: boolean;
  submitError: boolean;
}

/**
 * O ensaio em andamento: carrega as questões, guarda cada resposta no
 * navegador e pede confirmação antes de finalizar. Usado pela página
 * `/ensaio` (quem chega pela LP) e pela aba Ensaio do painel (quem já tem
 * conta e ainda não fez) — o que "finalizar" faz é de quem hospeda.
 *
 * No topo, para quem prefere o papel: **baixar o ensaio** (o arquivo público
 * da edição, em aba nova, se houver) e **fornecer respostas** (a folha do
 * gabarito, que preenche o ensaio inteiro de uma vez). Depois de fornecer, a
 * tela volta à questão 1 para a pessoa conferir — e o resto segue igual.
 *
 * **Sem as Previsões Aristo aqui, de propósito**: enquanto responde, a pessoa
 * não é puxada para fora do ensaio. Elas ficam no painel (`PainelPage`).
 */
export function EnsaioRunner({
  onFinish,
  isSubmitting,
  submitError,
}: EnsaioRunnerProps) {
  const examQuery = useExam();
  const { draft, confirm, replaceAll } = useSimuladoDraft();
  const [isConfirming, setIsConfirming] = useState(false);
  const [isProvidingAnswers, setIsProvidingAnswers] = useState(false);
  /**
   * Muda a cada folha confirmada. Vai na `key` do navegador de questões, que
   * então recomeça do zero — na questão 1, para a conferência.
   */
  const [reviewRound, setReviewRound] = useState(0);

  const questions = useMemo(
    () => (examQuery.data ? toSimuladoQuestions(examQuery.data) : []),
    [examQuery.data],
  );
  const answeredCount = Object.keys(draft.answers).length;

  /**
   * As alternativas da folha: as questões do ensaio vêm de provas diversas, e
   * a folha precisa caber a que tiver mais. Nunca menos que A–D.
   */
  const optionCount: 4 | 5 = questions.some(
    (question) => question.alternatives.length > 4,
  )
    ? 5
    : 4;

  /** Início e marcos de avanço, para o clique numa alternativa e a folha. */
  const trackProgress = (before: number, after: number) => {
    if (before === 0 && after > 0) {
      track({ event: 'ensaio_iniciado', total: questions.length });
    }
    for (const percentual of crossedMilestones(
      before,
      after,
      questions.length,
    )) {
      track({ event: 'ensaio_progresso', percentual });
    }
  };

  // "Baixar o ensaio" não depende das questões: aparece com elas carregando e
  // com a busca fora do ar (resolver no papel é a saída). "Fornecer
  // respostas" precisa saber quantas questões o ensaio tem.
  const hasEnsaioMaterial = useEnsaioMaterial() !== null;
  const header = (
    <>
      {hasEnsaioMaterial && !examQuery.isSuccess && (
        <div className="ensaio-tools">
          <p className="ensaio-tools-text">Prefere resolver no papel?</p>
          <div className="ensaio-tools-actions">
            <EnsaioDownloadLink />
          </div>
        </div>
      )}
    </>
  );

  if (examQuery.isPending) {
    return (
      <>
        {header}
        <div
          className="skeleton skeleton-question"
          aria-busy="true"
          aria-label="Carregando questões"
        />
      </>
    );
  }

  if (examQuery.isError) {
    return (
      <>
        {header}
        <div className="alert" role="alert">
          Não conseguimos carregar as questões agora.{' '}
          <button
            type="button"
            className="link-button"
            onClick={() => void examQuery.refetch()}
          >
            Tentar de novo
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {header}

      <div className="ensaio-tools">
        <p className="ensaio-tools-text">Prefere resolver no papel?</p>
        <div className="ensaio-tools-actions">
          <EnsaioDownloadLink />
          <button
            type="button"
            className="ghost"
            onClick={() => setIsProvidingAnswers(true)}
          >
            Fornecer respostas
          </button>
        </div>
      </div>

      {reviewRound > 0 && (
        <p className="notice stack-gap" role="status">
          Respostas preenchidas. Confira cada questão (clique em outra
          alternativa para corrigir) e finalize quando quiser.
        </p>
      )}
      {submitError && (
        <div className="alert stack-gap" role="alert">
          Não conseguimos enviar seu ensaio agora. Suas respostas continuam
          salvas. Tente finalizar de novo em instantes.
        </div>
      )}
      {isSubmitting && (
        <p className="notice stack-gap" role="status">
          Enviando seu ensaio…
        </p>
      )}

      <QuestionStepper
        key={reviewRound}
        questions={questions}
        answers={draft.answers}
        startAtFirst={reviewRound > 0}
        onAnswer={(number: number, answer: AnswerOption | null) => {
          const after =
            answeredCount +
            (answer && !draft.answers[number] ? 1 : 0) -
            (!answer && draft.answers[number] ? 1 : 0);
          confirm(number, answer);
          trackProgress(answeredCount, after);
        }}
        onFinish={() => setIsConfirming(true)}
        finishLabel="Finalizar ensaio"
      />

      <EnsaioAnswersModal
        open={isProvidingAnswers}
        totalQuestions={questions.length}
        optionCount={optionCount}
        initialAnswers={draft.answers}
        onClose={() => setIsProvidingAnswers(false)}
        onConfirm={(answers: Answers) => {
          const after = Object.keys(answers).length;
          replaceAll(answers);
          setIsProvidingAnswers(false);
          setReviewRound((round) => round + 1);
          track({
            event: 'ensaio_respostas_fornecidas',
            respondidas: after,
            total: questions.length,
          });
          trackProgress(answeredCount, after);
        }}
      />

      <FinishConfirmModal
        open={isConfirming}
        answeredCount={answeredCount}
        totalCount={questions.length}
        onCancel={() => setIsConfirming(false)}
        onConfirm={() => {
          setIsConfirming(false);
          track({
            event: 'ensaio_finalizado',
            respondidas: answeredCount,
            total: questions.length,
          });
          onFinish(toAnswersByQuestionId(questions, draft.answers));
        }}
      />
    </>
  );
}
